import * as React from "react";
import { IPersonaProps } from "office-ui-fabric-react/lib/Persona";
import { Label } from "office-ui-fabric-react/lib/Label";
import { CompactPeoplePicker, IBasePickerSuggestionsProps, ValidationState } from "office-ui-fabric-react/lib/Pickers";

interface IMyPeoplePickerProps {
    id: string;
    gridArea: string;
    labelText: string;
    isVisible: boolean;
    isRequired: boolean;
    onChange: (value) => void;
    peopleList: IPersonaProps[];
    submitClicked: boolean;
}

const suggestionProps: IBasePickerSuggestionsProps = {
    suggestionsHeaderText: 'Suggested People',
    mostRecentlyUsedHeaderText: 'Suggested Contacts',
    noResultsFoundText: 'No results found',
    loadingText: 'Loading',
    showRemoveButtons: true,
    suggestionsAvailableAlertText: 'People Picker Suggestions available',
    suggestionsContainerAriaLabel: 'Suggested contacts',
};

export function MyPeoplePicker(props: IMyPeoplePickerProps) {

    const { id, labelText, gridArea, isVisible, isRequired, onChange, peopleList, submitClicked } = props;
    const [selectedPeople, setSelectedPeople] = React.useState<IPersonaProps[]>([]);
    const [defaultSelected, setDefaultSelected] = React.useState<IPersonaProps[]>([]);
    const picker = React.useRef(null);

    const fetchPreselectedItem = () => {
        const defaultItem = peopleList.filter(user => user["isCurrentUser"]);
        if (defaultItem.length > 0) {
            setSelectedPeople(defaultItem);
            setDefaultSelected(defaultItem);
            onChange({ [id]: defaultItem[0]["key"] });
        }
    };

    React.useEffect(() => {
        if (selectedPeople.length <= 0 && isVisible && defaultSelected.length <= 0) {
            fetchPreselectedItem();
        }
    }, [selectedPeople]);

    const filterPromise = (personasToReturn: IPersonaProps[]): IPersonaProps[] | Promise<IPersonaProps[]> => {
        return personasToReturn;
    };

    const filterPersonasByText = (filterText: string): IPersonaProps[] => {
        return peopleList.filter(item => doesTextStartWith(item.text as string, filterText));
    };

    const onFilterChanged = (
        filterText: string,
        currentPersonas: IPersonaProps[],
        limitResults?: number,
    ): IPersonaProps[] | Promise<IPersonaProps[]> => {
        if (filterText) {
            let filteredPersonas: IPersonaProps[] = filterPersonasByText(filterText);
            filteredPersonas = removeDuplicates(filteredPersonas, currentPersonas);
            filteredPersonas = limitResults ? filteredPersonas.slice(0, limitResults) : filteredPersonas;
            return filterPromise(filteredPersonas);
        } else {
            return [];
        }
    };

    const showErrorClass = () => {
        if (submitClicked && selectedPeople.length <= 0 && isRequired) return "ms-Custom-Field__error";
        return "";
    };

    function doesTextStartWith(text: string, filterText: string): boolean {
        const arrFullName = text.split(" ");
        // Check if the first name start with filterText
        const firstNameStartsWith = arrFullName[0].toLowerCase().indexOf(filterText.toLowerCase()) === 0;
        // Check if the last name starts with filterText
        const lastNameStartsWith = arrFullName[1].toLowerCase().indexOf(filterText.toLowerCase()) === 0;
        return firstNameStartsWith || lastNameStartsWith;
    }

    function removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
        return personas.filter(persona => !listContainsPersona(persona, possibleDupes));
    }

    function listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
        if (!personas || !personas.length || personas.length === 0) {
            return false;
        }
        return personas.filter(item => item.text === persona.text).length > 0;
    }

    function getTextFromItem(persona: IPersonaProps): string {
        return persona.text as string;
    }

    function validateInput(input: string): ValidationState {
        if (input.indexOf('@') !== -1) {
            return ValidationState.valid;
        } else if (input.length > 1) {
            return ValidationState.warning;
        } else {
            return ValidationState.invalid;
        }
    }

    function onChangeSelected(items?: IPersonaProps[]) {
        const selectedItem = items[0];
        setSelectedPeople(items);
        if (selectedItem) {
            onChange({ [id]: selectedItem["key"] });
        } else {
            onChange({ [id]: null });
        }
    }

    return (
        <div
            className={`ms-Custom-Field ${!isVisible && 'ms-Hidden-Field'}`}
            style={{ gridColumn: gridArea, gridRow: gridArea }}
        >
            <Label className={"ms-Custom-Field__label"} required={isRequired}>
                {labelText}
            </Label>
            <CompactPeoplePicker
                componentRef={picker}
                selectedItems={selectedPeople}
                pickerSuggestionsProps={suggestionProps}
                itemLimit={1}
                resolveDelay={300}
                disabled={!isVisible}
                inputProps={{ required: isRequired }}
                styles={{ root: "ms-PeoplePicker__input", text: showErrorClass() }}
                onResolveSuggestions={onFilterChanged}
                getTextFromItem={getTextFromItem}
                onValidateInput={validateInput}
                onChange={onChangeSelected}
            />
        </div>
    );
}
